import type { TurnId } from "@cmmd-forge/contracts";
import { ChevronRightIcon, FileDiffIcon } from "lucide-react";

import type { TurnDiffFileChange } from "../../types";
import { summarizeTurnDiffStats } from "../../lib/turnDiffTree";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { ChangedFilesTree } from "./ChangedFilesTree";
import { DiffStatLabel, hasNonZeroStat } from "./DiffStatLabel";
import { VscodeEntryIcon } from "./VscodeEntryIcon";
import {
  changedFileName,
  selectChangedFilePreview,
  summarizeChangedFileScopes,
} from "./changedFilesPresentation";

interface ChangedFilesCardProps {
  readonly turnId: TurnId;
  readonly files: ReadonlyArray<TurnDiffFileChange>;
  readonly expanded: boolean;
  readonly showCompactPreview: boolean;
  readonly allDirectoriesExpanded: boolean;
  readonly resolvedTheme: "light" | "dark";
  readonly onExpandedChange: (expanded: boolean) => void;
  readonly onToggleAllDirectories: () => void;
  readonly onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
}

export function ChangedFilesCard(props: ChangedFilesCardProps) {
  const {
    turnId,
    files,
    expanded,
    showCompactPreview,
    allDirectoriesExpanded,
    resolvedTheme,
    onExpandedChange,
    onToggleAllDirectories,
    onOpenTurnDiff,
  } = props;
  const summaryStat = summarizeTurnDiffStats(files);
  const scopeSummary = summarizeChangedFileScopes(files);
  const previewFiles = selectChangedFilePreview(files);
  const compactPreviewVisible = showCompactPreview && !expanded;

  return (
    <section
      className="@container/changed-files mt-2 rounded-xl border border-border/70 bg-secondary/60 p-2"
      data-changed-files-state={
        expanded ? "expanded" : compactPreviewVisible ? "preview" : "collapsed"
      }
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-expanded={expanded}
          data-scroll-anchor-ignore
          className="min-w-0 flex-1 justify-start border-transparent px-2"
          onClick={() => onExpandedChange(!expanded)}
        >
          <ChevronRightIcon
            aria-hidden="true"
            className={cn("size-3.5 transition-transform", expanded ? "rotate-90" : null)}
          />
          <span className="truncate">
            {files.length} changed file{files.length === 1 ? "" : "s"}
          </span>
          {hasNonZeroStat(summaryStat) ? (
            <span className="shrink-0 font-mono text-[10px] tabular-nums">
              <DiffStatLabel additions={summaryStat.additions} deletions={summaryStat.deletions} />
            </span>
          ) : null}
        </Button>
        <div className="flex shrink-0 items-center gap-1.5">
          {expanded ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              data-scroll-anchor-ignore
              onClick={onToggleAllDirectories}
            >
              {allDirectoriesExpanded ? "Collapse folders" : "Expand folders"}
            </Button>
          ) : null}
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => onOpenTurnDiff(turnId, files[0]?.path)}
          >
            <FileDiffIcon className="size-3" />
            <span className="hidden @[24rem]/changed-files:inline">View diff</span>
          </Button>
        </div>
      </div>
      {expanded ? (
        <div className="mt-1.5">
          <ChangedFilesTree
            key={`changed-files-tree:${turnId}`}
            turnId={turnId}
            files={files}
            allDirectoriesExpanded={allDirectoriesExpanded}
            resolvedTheme={resolvedTheme}
            onOpenTurnDiff={onOpenTurnDiff}
          />
        </div>
      ) : compactPreviewVisible ? (
        <div className="px-2 pb-1.5 pt-1">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
            {scopeSummary.map((scope, index) => (
              <span key={scope.label} className="inline-flex items-center gap-1">
                {index > 0 ? <span aria-hidden="true">·</span> : null}
                <span className="font-mono text-foreground/75">{scope.label}</span>
                <span>
                  {scope.fileCount} file{scope.fileCount === 1 ? "" : "s"}
                </span>
              </span>
            ))}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {previewFiles.map((file) => (
              <Button
                key={file.path}
                type="button"
                size="xs"
                variant="outline"
                className="max-w-48 font-mono text-[10px]"
                aria-label={`Open ${file.path} in the diff`}
                onClick={() => onOpenTurnDiff(turnId, file.path)}
              >
                <VscodeEntryIcon
                  pathValue={file.path}
                  kind="file"
                  theme={resolvedTheme}
                  className="size-3 text-muted-foreground/70"
                />
                <span className="truncate">{changedFileName(file.path)}</span>
              </Button>
            ))}
            <Button type="button" size="xs" variant="ghost" onClick={() => onExpandedChange(true)}>
              Show all {files.length}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
